import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FaArrowDownShortWide, FaArrowUpShortWide } from "react-icons/fa6";

/**
 * Custom scrollbar rendered as an HTML element — always visible on all devices
 * including iOS where native scrollbars auto-hide.
 */
export function AlwaysVisibleScrollbar({ scrollContainerRef, statusColor }) {
  const trackRef = useRef(null);
  const [thumbRatio, setThumbRatio] = useState(1);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);

  const handleKeyDown = useCallback(
    (e) => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const key = e.key;
      const isArrow = key === "ArrowLeft" || key === "ArrowRight";
      const isPage = key === "PageUp" || key === "PageDown";
      const isHomeEnd = key === "Home" || key === "End";
      if (!isArrow && !isPage && !isHomeEnd) return;

      e.preventDefault();

      const smallStep = e.shiftKey ? 200 : 60;
      const pageStep = Math.max(240, Math.floor(el.clientWidth * 0.9));
      const maxLeft = el.scrollWidth - el.clientWidth;

      if (key === "Home") {
        el.scrollLeft = 0;
        return;
      }
      if (key === "End") {
        el.scrollLeft = maxLeft;
        return;
      }

      const delta =
        key === "ArrowLeft"
          ? -smallStep
          : key === "ArrowRight"
            ? smallStep
            : key === "PageUp"
              ? -pageStep
              : pageStep;

      el.scrollLeft = Math.max(0, Math.min(maxLeft, el.scrollLeft + delta));
    },
    [scrollContainerRef],
  );

  // Recalculate thumb size/position when content changes or resizes
  const recalc = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    const needs = scrollWidth > clientWidth;
    setNeedsScroll(needs);
    if (needs) {
      const ratio = clientWidth / scrollWidth;
      setThumbRatio(ratio);
      setThumbLeft((scrollLeft / scrollWidth) * 100);
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    recalc();
    const el = scrollContainerRef.current;
    if (!el) return;

    el.addEventListener("scroll", recalc, { passive: true });
    window.addEventListener("resize", recalc);

    // Also observe DOM changes (columns may load async)
    const observer = new ResizeObserver(recalc);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", recalc);
      window.removeEventListener("resize", recalc);
      observer.disconnect();
    };
  }, [recalc, scrollContainerRef]);

  // Drag handlers (mouse + touch)
  const onDragStart = useCallback(
    (clientX) => {
      dragging.current = true;
      dragStartX.current = clientX;
      dragStartScroll.current = scrollContainerRef.current?.scrollLeft || 0;
      document.body.style.userSelect = "none";
    },
    [scrollContainerRef]
  );

  const onDragMove = useCallback(
    (clientX) => {
      if (!dragging.current) return;
      const track = trackRef.current;
      const el = scrollContainerRef.current;
      if (!track || !el) return;
      const trackWidth = track.clientWidth;
      const dx = clientX - dragStartX.current;
      const scrollRange = el.scrollWidth - el.clientWidth;
      const scrollDelta = (dx / trackWidth) * el.scrollWidth;
      el.scrollLeft = Math.max(
        0,
        Math.min(scrollRange, dragStartScroll.current + scrollDelta)
      );
    },
    [scrollContainerRef]
  );

  const onDragEnd = useCallback(() => {
    dragging.current = false;
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    const moveHandler = (e) => onDragMove(e.touches ? e.touches[0].clientX : e.clientX);
    const endHandler = () => onDragEnd();

    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", endHandler);
    window.addEventListener("touchmove", moveHandler, { passive: true });
    window.addEventListener("touchend", endHandler);

    return () => {
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", endHandler);
      window.removeEventListener("touchmove", moveHandler);
      window.removeEventListener("touchend", endHandler);
    };
  }, [onDragMove, onDragEnd]);

  // Click on track to jump
  const onTrackClick = useCallback(
    (e) => {
      const track = trackRef.current;
      const el = scrollContainerRef.current;
      if (!track || !el) return;
      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const fraction = clickX / rect.width;
      el.scrollLeft = fraction * el.scrollWidth - el.clientWidth / 2;
    },
    [scrollContainerRef]
  );

  if (!needsScroll) return null;

  const thumbWidth = Math.max(thumbRatio * 100, 10); // min 10% so it's tappable
  const thumbClass = statusColor === "dark"
    ? "!bg-red-800 hover:!bg-red-900 active:!bg-red-900"
    : statusColor === "light"
    ? "!bg-red-600 hover:!bg-red-700 active:!bg-red-700"
    : "";

  return (
    <div
      ref={trackRef}
      className="custom-scrollbar-track"
      onClick={onTrackClick}
      tabIndex={0}
      role="scrollbar"
      aria-label="Horizontal scrollbar"
      onKeyDown={handleKeyDown}
    >
      <div
        className={`custom-scrollbar-thumb ${thumbClass}`}
        style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragStart(e.clientX);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          onDragStart(e.touches[0].clientX);
        }}
      />
    </div>
  );
}

/** Matrix “address” columns — first match in column order gets sticky-left on small screens */
const STICKY_ADDRESS_DATA_INDEXES = [
  "_computed_address",
  "short_address",
  "very_short_address",
];

function useIsDesktop(breakpointPx = 768) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia?.(`(min-width: ${breakpointPx}px)`)?.matches ?? true;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(min-width: ${breakpointPx}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpointPx]);

  return isDesktop;
}

// Derived "$ per SF" columns. They are computed at render time and can be
// inserted anywhere in the admin column config, including inside the frozen
// leading block — see stickyCount below.
const PER_SF_DATA_INDEXES = new Set([
  "_computed_asking_price_per_sf",
  "_computed_prev_sold_price_per_sf",
  "_computed_asking_rent_per_sf",
  "_computed_sold_price_per_sf",
  "_computed_rented_price_per_sf",
]);

// Matrices whose leading block is frozen on mobile, and the price / date columns
// that block can contain. The price and date field keys differ per matrix
// (mls_sold_price vs rented_price; some communities use price_or_rent for both),
// so match on the whole set rather than one key per matrix.
const MOBILE_FROZEN_STATUSES = new Set(["SALE", "RENT", "SOLD", "RENTED"]);
const FROZEN_PRICE_DATA_INDEXES = new Set([
  "price_or_rent",
  "mls_sold_price",
  "sold_price",
  "rented_price",
]);
const FROZEN_DATE_DATA_INDEXES = new Set([
  "date_became_sold",
  "date_became_rented",
]);

const CURRENCY_COLS = new Set([
  "price_or_rent",
  "sold_price",
  "mls_sold_price",
  "rented_price",
  "original_asking_price",
  "original_asking_rent",
  "previous_sold_price",
  "real_estate_tax",
  "county_assessed_value",
  "furnished_annual",
  "furnished_seasonal",
  "furnished_off_season",
  "unfurnished_annual",
  "unfurnished_seasonal",
  "unfurnished_off_season",
  "_computed_asking_price_per_sf",
  "_computed_prev_sold_price_per_sf",
  "_computed_asking_rent_per_sf",
  "_computed_sold_price_per_sf",
  "_computed_rented_price_per_sf",
  "_computed_sold_dollar_discount",
  "_computed_sold_percent_discount",
  "_computed_rented_dollar_discount",
  "_computed_rented_percent_discount",
]);

function getColumnWidthPx(column, status, isDesktop, isPhone = false) {
  const sortKey = column.sortKey || column.dataIndex || column.key;
  const statusUpper = String(status || "").toUpperCase();
  // On mobile every matrix freezes a leading block — SALE/RENT two columns,
  // SOLD/RENTED three (address, price, date) — so each frozen column needs a
  // definite width to compute the sticky-left offsets. These numbers match the
  // rendered width so all four matrices open equally narrow.
  //   72px: the address floor — "ADDRESS" is nowrap and needs ~68px at text-xs.
  //   price: cells are text-xs below 640px and text-sm above it, so the same
  //     "$3,199,000" (widest price in the data) needs ~82px on a portrait phone
  //     but ~91px on a landscape phone / small tablet. Undersizing clips the
  //     leading digits, since currency cells are right-aligned and nowrap.
  //   date: "08/18/26" needs ~70px at text-xs and ~78px at text-sm. The header
  //     never sets the floor — multi-word headers stack one word per line, so
  //     "SOLD DATE" renders as SOLD / DATE and measures narrower than the value.
  if (!isDesktop && MOBILE_FROZEN_STATUSES.has(statusUpper)) {
    if (STICKY_ADDRESS_DATA_INDEXES.includes(column.dataIndex)) return 72;
    if (FROZEN_PRICE_DATA_INDEXES.has(column.dataIndex)) return isPhone ? 84 : 96;
    if (FROZEN_DATE_DATA_INDEXES.has(column.dataIndex)) return isPhone ? 80 : 88;
  }
  const isNumericColumn = [
    "bedrooms",
    "bathrooms",
    "half_bathrooms",
    "square_feet",
    "sf_under_ac",
  ].includes(sortKey);
  // Currency columns hold large dollar amounts (e.g. "$3,650,000"). When such a
  // column is frozen, the default 80px is too narrow: the right-aligned value
  // overflows its left edge and slips under the sticky column beside it, hiding
  // the leading digits ("$1,425,000" → ",425,000"). Give them more room.
  const isCurrencyColumn = CURRENCY_COLS.has(column.dataIndex);
  const defaultWidth = isNumericColumn
    ? "55px"
    : isCurrencyColumn
      ? "110px"
      : "80px";
  const fallback = isNumericColumn ? 55 : isCurrencyColumn ? 110 : 80;
  const raw = column.width || column.minWidth || defaultWidth;
  const n = parseInt(String(raw).replace(/px/gi, "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function findStickyAddressColumnIndex(columns, dataIndexes) {
  return columns.findIndex((c) => dataIndexes.includes(c.dataIndex));
}

function computeStickyLeftPx(columns, stickyIndex, status, isDesktop, isPhone) {
  if (stickyIndex <= 0) return 0;
  let sum = 0;
  for (let i = 0; i < stickyIndex; i++) {
    sum += getColumnWidthPx(columns[i], status, isDesktop, isPhone);
  }
  return sum;
}

function computeStickyLeftOffsets(columns, count, status, isDesktop, isPhone) {
  const offsets = [];
  let sum = 0;
  for (let i = 0; i < columns.length; i++) {
    offsets[i] = sum;
    if (i < count) sum += getColumnWidthPx(columns[i], status, isDesktop, isPhone);
  }
  return offsets;
}

// The same cumulative sum as computeStickyLeftOffsets, over the widths the
// browser actually laid out instead of the ones getColumnWidthPx declares.
// The declared number is only a floor: frozen cells are nowrap inside a
// table-layout:auto table, so a value that needs more room than the floor
// widens its column and the browser never tells anyone. Every frozen column to
// its right therefore has to start that much further along, or it parks under
// its neighbour — and the leading columns carry the higher z-index, so it is
// the newcomer that loses its first characters ("$1,450,000" reads ",450,000").
function offsetsFromMeasuredWidths(widths, columnCount) {
  const offsets = [];
  let sum = 0;
  for (let i = 0; i < columnCount; i++) {
    offsets[i] = sum;
    if (i < widths.length) sum += widths[i];
  }
  return offsets;
}

// Two measurements mean "the same width" when they differ by less than this.
// Sub-pixel jitter is normal — a font swapping in, a scrollbar appearing, the
// browser re-resolving a fractional layout — and an exact comparison would turn
// that jitter into a measure → setState → render → measure loop.
const STICKY_WIDTH_EPSILON_PX = 0.5;

function stickyWidthsMatch(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i] - b[i]) > STICKY_WIDTH_EPSILON_PX) return false;
  }
  return true;
}

function parseSortableNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const normalized = String(value)
    .trim()
    .replace(/[$,%\s,]/g, "")
    .replace(/^\((.*)\)$/, "-$1");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getColumnAlignClass(column) {
  const align = String(column?.align || "").toLowerCase();
  if (align === "left") return "text-left";
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "";
}

function shouldWrapColumn(column) {
  return Boolean(column?.allowWrap);
}

function shouldWrapHeader(column) {
  return Boolean(column?.wrapHeader);
}

/**
 * Keeps the column-header row visible while its table scrolls past, the way
 * `position: sticky` would — except sticky cannot do it here. The row lives
 * inside the matrix's horizontal scroll container, and a box with
 * `overflow-x: auto` computes `overflow-y` to `auto` as well, which makes it the
 * scrollport every sticky descendant is measured against. That box never scrolls
 * vertically, so a sticky header row inside it simply rides away with the page.
 *
 * So the row is moved by hand instead: on every scroll frame it is translated
 * down to the bottom edge of `floatBelowRef` (the matrix title bar, itself
 * sticky) and clamped to the bottom of its own table, so it never outlives the
 * rows it labels. translateY does not touch layout, so the column widths and the
 * frozen-left columns keep working untouched.
 *
 * @param floatBelowRef ref to the element the row should come to rest under
 * @param deps          values that mean the row itself came or went, so the
 *                      listeners have to be re-bound to a new node
 */
function useFloatingHeaderRow(floatBelowRef, deps) {
  const tableRef = useRef(null);
  const headRef = useRef(null);
  const shiftRef = useRef(0);

  useEffect(() => {
    const anchor = floatBelowRef?.current;
    const head = headRef.current;
    if (!floatBelowRef || !anchor || !head) return undefined;

    const apply = () => {
      const table = tableRef.current;
      const thead = headRef.current;
      const below = floatBelowRef.current;
      if (!table || !thead || !below) return;
      // translateY leaves layout alone, so these three stay stable as the row moves
      const tableBox = table.getBoundingClientRect();
      const rowHeight = thead.getBoundingClientRect().height;
      const restLine = below.getBoundingClientRect().bottom;
      const shift = Math.max(
        0,
        Math.min(
          Math.round(restLine - tableBox.top),
          Math.round(tableBox.height - rowHeight),
        ),
      );
      if (shift === shiftRef.current) return;
      shiftRef.current = shift;
      thead.style.transform = shift ? `translateY(${shift}px)` : "";
    };

    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        apply();
      });
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Row heights and table height move with the data, the wrapped headers and
    // the viewport width, and none of those fire a scroll event.
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(onScroll) : null;
    if (observer) {
      observer.observe(head);
      if (tableRef.current) observer.observe(tableRef.current);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (observer) observer.disconnect();
      head.style.transform = "";
      shiftRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floatBelowRef, ...deps]);

  return { tableRef, headRef, floating: Boolean(floatBelowRef) };
}

const CustomTable = ({
  columns,
  dataSource,
  onClickRow,
  onClickCell,
  linkableColumns,
  status,
  showSortIcons = true,
  noInternalScroll = false,
  emptyMessage,
  /** When true (default), keep the ADDRESS column pinned to the left while scrolling horizontally */
  stickyAddressColumn = true,
  /** Ref to the element the column-header row should float beneath (the matrix
   *  title bar). Passing it turns the header row into a floating one — see
   *  useFloatingHeaderRow. Leave it out and the row scrolls away as usual. */
  floatBelowRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeColumn, setActiveColumn] = useState(null);
  const [activeButton, setActiveButton] = useState(null);
  const [value, setValue] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [previousColumn, setPreviousColumn] = useState(null);
  const [sortedData, setSortedData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const scrollContainerRef = useRef(null);
  const {
    tableRef,
    headRef,
    floating: floatingHeaderRow,
    // Lengths, not the arrays themselves: both are rebuilt on every render of the
    // matrix, and re-running only when the row is added or removed keeps the
    // listeners in place. Size changes are the ResizeObserver's job.
  } = useFloatingHeaderRow(floatBelowRef, [columns?.length, dataSource?.length]);
  const isDesktop = useIsDesktop(768);
  // Below 640px the cells render at text-xs instead of text-sm, so the frozen
  // columns can be tighter there than on a landscape phone / small tablet.
  const isPhone = !useIsDesktop(640);

  const stickyColIndex = useMemo(
    () =>
      stickyAddressColumn && Array.isArray(columns) && columns.length > 0
        ? findStickyAddressColumnIndex(columns, STICKY_ADDRESS_DATA_INDEXES)
        : -1,
    [columns, stickyAddressColumn],
  );
  // What the declared widths can promise on their own. Both "declared" values
  // are the fallback the measured ones below replace as soon as there is a
  // layout to read; see the measurement block for why guessing is not enough.
  const declaredStickyLeftPx = useMemo(
    () =>
      stickyColIndex >= 0
        ? computeStickyLeftPx(columns, stickyColIndex, status, isDesktop, isPhone)
        : 0,
    [columns, stickyColIndex, status, isDesktop, isPhone],
  );
  const hasStickyAddress = stickyColIndex >= 0;

  // Matrices: pin the first N columns to the left wall. The DB column config is
  // ordered so these leading columns are exactly the ones that should stick:
  // - Sale:   Address, Current Asking Price      → Desktop 2, Mobile 2
  // - Rent:   Address, Current Asking Rent       → Desktop 2, Mobile 2
  // - Sold:   Address, Sold Price(, Sold Date)     → Desktop 3, Mobile 2
  // - Rented: Address, Rented Price(, Rented Date) → Desktop 3, Mobile 2
  // On desktop SOLD and RENTED also freeze the date: scrolling right must never
  // carry the address, the price or the date out of view, because those three
  // are what identifies the row. A narrow phone cannot afford all three — three
  // frozen columns eat ~270px of a 390px screen, leaving almost nothing to
  // scroll — so below the desktop breakpoint the date gives up its slot.
  // The counts above are positional, but the admin config may insert a derived
  // "$ per SF" column inside that leading run (e.g. SOLD PER SF right of SOLD
  // PRICE). Counting it would evict a real column (SOLD DATE) from the frozen
  // block. So the count below is a count of *non-derived* columns: the boundary
  // extends past any per-SF column it encounters instead of spending a slot on it.
  const stickyCount = useMemo(() => {
    const s = String(status || "").toUpperCase();
    let target = 0;
    if (s === "SALE" || s === "RENT") target = 2;
    else if (s === "SOLD" || s === "RENTED") target = isDesktop ? 3 : 2;
    if (target <= 0) return 0;

    const list = Array.isArray(columns) ? columns : [];
    let realSeen = 0;
    for (let i = 0; i < list.length; i++) {
      if (!PER_SF_DATA_INDEXES.has(list[i]?.dataIndex)) realSeen += 1;
      if (realSeen >= target) return i + 1;
    }
    return list.length;
  }, [status, columns, isDesktop]);

  const declaredStickyLeftOffsets = useMemo(() => {
    const safeCount = Math.max(0, Math.min(stickyCount, columns?.length ?? 0));
    return computeStickyLeftOffsets(columns || [], safeCount, status, isDesktop, isPhone);
  }, [columns, stickyCount, status, isDesktop, isPhone]);

  // How many leading header cells have to be measured: every frozen column, and
  // — for the address-only case, where stickyCount is 0 and a lone ADDRESS
  // column sticks — everything to its left as well, because that column's
  // offset is exactly the combined width of the columns it comes after.
  const measuredCount = useMemo(() => {
    const total = columns?.length ?? 0;
    return Math.max(
      Math.min(Math.max(stickyCount, 0), total),
      hasStickyAddress ? Math.min(stickyColIndex + 1, total) : 0,
    );
  }, [columns?.length, stickyCount, hasStickyAddress, stickyColIndex]);

  // Column identity, not just how many: a config change can swap the leading
  // columns without changing the count, and since the cells are keyed by
  // column.key React hands back brand-new <th> nodes for the observer to follow.
  const measuredColumnsKey = useMemo(
    () =>
      (Array.isArray(columns) ? columns : [])
        .slice(0, measuredCount)
        .map((c) => c?.key)
        .join("|"),
    [columns, measuredCount],
  );

  // The frozen header cells themselves. There is exactly one set of them even
  // when the header row floats: useFloatingHeaderRow moves this same <thead>
  // with a transform rather than rendering a second copy, and a transform does
  // not touch layout — so these nodes are the real columns, at their real width.
  const stickyHeadCellRefs = useRef([]);
  const [measuredStickyWidths, setMeasuredStickyWidths] = useState(null);

  const measureStickyWidths = useCallback(() => {
    if (measuredCount <= 0) {
      setMeasuredStickyWidths((prev) => (prev === null ? prev : null));
      return;
    }
    const next = new Array(measuredCount);
    for (let i = 0; i < measuredCount; i++) {
      const cell = stickyHeadCellRefs.current[i];
      // Nothing mounted yet, or nothing to measure with (jsdom, a display:none
      // ancestor) — both report their way out here. Keep whatever is already in
      // state: a declared offset is a few pixels wrong, a zeroed one collapses
      // the whole frozen block onto the left wall.
      if (!cell) return;
      // getBoundingClientRect, not offsetWidth: offsetWidth rounds to whole
      // pixels and three frozen columns of rounding error is a visible seam.
      const width = cell.getBoundingClientRect().width;
      if (!Number.isFinite(width) || width <= 0) return;
      next[i] = width;
    }
    // Loop safety: hand the previous reading straight back when nothing moved by
    // more than half a pixel. React bails out on identical state, so the effect
    // below cannot re-render itself into a measure → setState → measure loop.
    // The reading carries the columns it was taken from: a config swap can keep
    // measuredCount identical while changing which columns lead, and the old
    // set's widths would then be applied to the new one for a commit.
    setMeasuredStickyWidths((prev) =>
      prev &&
      prev.key === measuredColumnsKey &&
      stickyWidthsMatch(prev.widths, next)
        ? prev
        : { key: measuredColumnsKey, widths: next },
    );
  }, [measuredCount, measuredColumnsKey]);

  // Re-measure whenever the layout can have moved. The observer covers what no
  // dependency can see — a font finishing loading, the phone rotating, a data
  // refresh that kept the row count but brought a longer address — and writing
  // `left` never changes a cell's width, so it cannot feed itself either.
  useEffect(() => {
    measureStickyWidths();
    const cells = stickyHeadCellRefs.current
      .slice(0, measuredCount)
      .filter(Boolean);
    if (cells.length === 0) return undefined;

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureStickyWidths);
      return () => window.removeEventListener("resize", measureStickyWidths);
    }

    const observer = new ResizeObserver(measureStickyWidths);
    cells.forEach((cell) => observer.observe(cell));
    return () => observer.disconnect();
    // The last five are not read inside the effect; each one is listed because
    // it means the cells to observe — or what sets their width — just changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    measureStickyWidths,
    measuredCount,
    measuredColumnsKey,
    columns?.length,
    dataSource?.length,
    isDesktop,
    isPhone,
    status,
  ]);

  // Measured where the layout has answered, declared until it does. The two
  // offsets are resolved together because the address-only offset has to come
  // from the same source as the frozen-block offsets: mixing a measured wall
  // with a declared one would leave the ADDRESS column standing where the
  // columns to its left do not actually end. A reading whose key or length no
  // longer matches the current leading columns belongs to a previous set, so it
  // is discarded for the one render it takes the effect to catch up.
  const { stickyLeftOffsets, stickyLeftPx } = useMemo(() => {
    const measured =
      measuredStickyWidths &&
      measuredStickyWidths.key === measuredColumnsKey &&
      measuredStickyWidths.widths.length === measuredCount
        ? offsetsFromMeasuredWidths(measuredStickyWidths.widths, columns?.length ?? 0)
        : null;
    if (!measured) {
      return {
        stickyLeftOffsets: declaredStickyLeftOffsets,
        stickyLeftPx: declaredStickyLeftPx,
      };
    }
    return {
      stickyLeftOffsets: measured,
      stickyLeftPx: stickyColIndex >= 0 ? measured[stickyColIndex] ?? 0 : 0,
    };
  }, [
    measuredStickyWidths,
    measuredCount,
    measuredColumnsKey,
    columns?.length,
    declaredStickyLeftOffsets,
    declaredStickyLeftPx,
    stickyColIndex,
  ]);

  const displayedData =
    sortedData.length > 0
      ? sortedData
      : filteredData.length > 0
      ? filteredData
      : dataSource;

  useEffect(() => {
    setFilteredData([]);
    setSortedData(
      sortConfig.key
        ? sortData(dataSource, sortConfig.key, sortConfig.direction)
        : dataSource
    );
  }, [dataSource]);

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (
        key === "listing_date" ||
        key === "date_became_sold" ||
        key === "date_became_rented" ||
        key === "available_date" ||
        key === "date_became_active" ||
        key === "date_became_pending" ||
        key === "date_became_off_market" ||
        key === "date_became_incomplete" ||
        key === "date_became_cancelled" ||
        key === "date_became_withdrawn" ||
        key === "date_became_expired" ||
        key === "previous_sold_date"
      ) {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (
        [
          "price_or_rent",
          "sold_price",
          "mls_sold_price",
          "rented_price",
          "original_asking_price",
          "original_asking_rent",
          "previous_sold_price",
          "real_estate_tax",
          "county_assessed_value",
          "dom",
          "furnished_annual",
          "furnished_seasonal",
          "furnished_off_season",
          "unfurnished_annual",
          "unfurnished_seasonal",
          "unfurnished_off_season",
          "bedrooms",
          "bathrooms",
          "half_bathrooms",
          "square_feet",
          "sf_under_ac",
          "_computed_asking_price_per_sf",
          "_computed_prev_sold_price_per_sf",
          "_computed_asking_rent_per_sf",
          "_computed_sold_price_per_sf",
          "_computed_rented_price_per_sf",
          "_computed_sold_dollar_discount",
          "_computed_sold_percent_discount",
          "_computed_rented_dollar_discount",
          "_computed_rented_percent_discount",
        ].includes(key)
      ) {
        aValue = parseSortableNumber(aValue);
        bValue = parseSortableNumber(bValue);
      } else {
        aValue = aValue ? String(aValue).toLowerCase() : "";
        bValue = bValue ? String(bValue).toLowerCase() : "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }
    });
  };

  useEffect(() => {
    if (!displayedData || displayedData.length === 0) return;

    if (!sortConfig.key) {
      let key = "";
      let direction = "";

      if (status === "Sale" || status === "Rent") {
        key = "price_or_rent";
        direction = "asc";
      } else if (status === "Sold") {
        key = "date_became_sold";
        direction = "desc";
      } else if (status === "Rented") {
        key = "date_became_rented";
        direction = "desc";
      }

      if (key) {
        setSortConfig({ key, direction });
        setSortedData(sortData(displayedData, key, direction));
      }
    }
  }, [status, displayedData]);

  const handleSort = (sortKey) => {
    const newDirection =
      sortConfig.key === sortKey && sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({ key: sortKey, direction: newDirection });

    setSortedData((prevSortedData) =>
      sortData(
        prevSortedData.length ? prevSortedData : dataSource,
        sortKey,
        newDirection
      )
    );
  };

  const buttonData = [
    {
      label: "Greater than or equal",
      filter: (columnKey, value) => (item) =>
        Number(item[columnKey]) >= Number(value),
    },
    {
      label: "Less than or equal",
      filter: (columnKey, value) => (item) =>
        Number(item[columnKey]) <= Number(value),
    },
    {
      label: "Equal",
      filter: (columnKey, value) => (item) =>
        Number(item[columnKey]) === Number(value),
    },
    {
      label: "Between",
      filter: (columnKey, value) => (item) => {
        const [min, max] = value.split(",").map(Number);
        return Number(item[columnKey]) >= min && Number(item[columnKey]) <= max;
      },
    },
    {
      label: "Contains",
      filter: (columnKey, value) => (item) =>
        item[columnKey].toLowerCase().includes(value.toLowerCase()),
    },
  ];

  // Property dates are calendar days stored at UTC midnight ("2024-02-22T00:00:00.000Z").
  // Format them in UTC — without timeZone the browser's local zone rolls any US visitor
  // back to the previous day (a 2/22 sold date would read 02/21).
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC",
    }).format(date);
  };

  const isEmpty = displayedData.length === 0;
  const statusUpper = (status || "").toUpperCase();
  const isDarkRed = statusUpper === "SOLD" || statusUpper === "RENTED";
  const statusColor = isDarkRed ? "dark" : "light";
  const headerBg = "#FDE8DF";

  let showTopScrollbar = false;
  let showBottomScrollbar = !isEmpty;

  const tableContent = (
        <div>
          {isEmpty && emptyMessage ? (
            <div className="px-2 py-3 text-left text-red-600 font-semibold bg-white text-base antialiased">
              {emptyMessage}
            </div>
          ) : (
          <table
            ref={tableRef}
            className={`w-full text-sm text-left text-gray-500 rtl:text-right table-auto ${
              (hasStickyAddress || stickyCount > 0) ? "border-separate border-spacing-0" : "border-collapse"
            }`}
          >
            {/* A floating header row is translated, and a transform makes the row
                its own stacking context — z-index 41 is what lifts that context
                over the frozen columns in the body (up to 20) while staying under
                the matrix title bar (50). position is what makes z-index count:
                sticky with no offset never moves the row on its own. */}
            <thead
              ref={headRef}
              className={floatingHeaderRow ? "sticky z-[41]" : ""}
              style={{ backgroundColor: headerBg }}
            >
              <tr>
                {columns.map((column, colIndex) => {
                  const sortKey = column.sortKey || column.dataIndex || column.key;
                  const isStickyGroupCol = stickyCount > 0 && colIndex < stickyCount;
                  const isStickyAddressCol =
                    hasStickyAddress && colIndex === stickyColIndex;
                  const isStickyCol = isStickyGroupCol || isStickyAddressCol;
                  const headerAlignClass =
                    getColumnAlignClass(column) ||
                    (column.nowrap ? "text-left" : "text-center");
                  return (
                    // The ref is what makes the offsets true: the inline width
                    // below is a floor the browser is free to exceed, so the
                    // sticky-left offsets are summed from what these cells
                    // measure, not from what they ask for.
                    <th
                      key={column.key}
                      ref={
                        colIndex < measuredCount
                          ? (el) => {
                              stickyHeadCellRefs.current[colIndex] = el;
                            }
                          : undefined
                      }
                      className={`px-2 py-2 font-bold text-blue-950 border border-black text-xs sm:text-sm align-bottom cursor-pointer transition-colors ${
                        isStickyCol
                          ? "sticky z-30 shadow-[2px_0_6px_rgba(0,0,0,0.12)]"
                          : ""
                      }`}
                      style={{
                        minWidth: isStickyGroupCol ? `${getColumnWidthPx(column, status, isDesktop, isPhone)}px` : "max-content",
                        width: isStickyGroupCol ? `${getColumnWidthPx(column, status, isDesktop, isPhone)}px` : undefined,
                        whiteSpace:
                          shouldWrapHeader(column)
                            ? "normal"
                            : column.nowrap || !shouldWrapColumn(column)
                            ? "nowrap"
                            : "normal",
                        verticalAlign: "bottom",
                        ...(isStickyCol
                          ? {
                              left: isStickyGroupCol
                                ? stickyLeftOffsets[colIndex] ?? 0
                                : stickyLeftPx,
                              backgroundColor: headerBg,
                              zIndex: 40 - colIndex,
                            }
                          : {}),
                      }}
                      onClick={() => handleSort(sortKey)}
                    >
                      <div
                        className="flex gap-1 items-end"
                      >
                        {[
                          "ASKING PRICE",
                          "ASKING RENT",
                          "CURRENT ASKING PRICE",
                          "CURRENT ASKING RENT",
                        ].includes(
                          String(column.title || "").toUpperCase()
                        ) ? (
                          <div className="leading-tight w-full flex flex-col items-center">
                            {String(column.title || "")
                              .toUpperCase()
                              .startsWith("CURRENT ") && (
                              <span className="uppercase tracking-wide text-xs sm:text-sm">
                                CURRENT
                              </span>
                            )}
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              ASKING
                            </span>
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              {String(column.title || "")
                                .toUpperCase()
                                .endsWith("PRICE")
                                ? "PRICE"
                                : "RENT"}
                            </span>
                          </div>
                        ) : column.title === "LIVING SF" ||
                          column.title === "LIVING" ? (
                          <div className="leading-tight w-full flex flex-col items-center">
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              LIVING
                            </span>
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              SF
                            </span>
                          </div>
                        ) : column.title === "PRVT POOL" ? (
                          <div className="leading-tight w-full flex flex-col items-center">
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              PRVT
                            </span>
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              POOL
                            </span>
                          </div>
                        ) : column.title === "CVRD GAR" ? (
                          <div className="leading-tight w-full flex flex-col items-center">
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              CVRD
                            </span>
                            <span className="uppercase tracking-wide text-xs sm:text-sm">
                              GAR
                            </span>
                          </div>
                        ) : typeof column.title === "object" && column.title !== null ? (
                          <div
                            className={`leading-tight w-full flex flex-col ${
                              headerAlignClass === "text-left"
                                ? "items-start"
                                : headerAlignClass === "text-right"
                                  ? "items-end"
                                  : "items-center"
                            } uppercase text-xs sm:text-sm`}
                          >
                            {column.title}
                          </div>
                        ) : (
                          <p className={`${headerAlignClass} w-full uppercase text-xs sm:text-sm`}>
                            {column.title}
                          </p>
                        )}
                        {showSortIcons && sortConfig.key === sortKey && (
                          <span className="ml-1">
                            {sortConfig.direction === "asc" ? (
                              <FaArrowUpShortWide
                                className="text-blue-600"
                                size={12}
                              />
                            ) : (
                              <FaArrowDownShortWide
                                className="text-blue-600"
                                size={12}
                              />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {
                displayedData.map((row, index) => {
                  const hasAnyCellHandler = !!onClickCell;
                  return (
                  <tr
                    key={row.id || index}
                    className={`group border-b hover:bg-gray-100 even:bg-green-100 ${!hasAnyCellHandler ? "cursor-pointer" : ""}`}
                    style={{ height: "48px" }}
                    onClick={() => !hasAnyCellHandler && onClickRow && onClickRow(row)}
                  >
                  {columns.map((column, colIndex) => {
                    const isLinkable = linkableColumns && linkableColumns.has(column.dataIndex);
                    const isStickyGroupCol = stickyCount > 0 && colIndex < stickyCount;
                    const isStickyAddressCol =
                      hasStickyAddress && colIndex === stickyColIndex;
                    const isStickyCol = isStickyGroupCol || isStickyAddressCol;
                    const stickyRowBg =
                      index % 2 === 1 ? "bg-green-100" : "bg-white";
                    const cellAlignClass =
                      CURRENCY_COLS.has(column.dataIndex)
                        ? "text-right"
                        : getColumnAlignClass(column) ||
                          (column.nowrap ? "text-left" : "text-center");
                    return (
                      <td
                        key={column.key}
                        className={`px-2 py-2 border border-black text-xs sm:text-sm ${isLinkable ? "cursor-pointer" : ""} ${
                          isStickyCol
                            ? `sticky z-10 shadow-[2px_0_6px_rgba(0,0,0,0.12)] ${stickyRowBg} group-hover:bg-gray-100`
                            : ""
                        }`}
                        style={{
                          minWidth: isStickyGroupCol ? `${getColumnWidthPx(column, status, isDesktop, isPhone)}px` : "max-content",
                          width: isStickyGroupCol ? `${getColumnWidthPx(column, status, isDesktop, isPhone)}px` : undefined,
                          whiteSpace:
                            column.nowrap || !shouldWrapColumn(column)
                              ? "nowrap"
                              : "normal",
                          ...(isStickyCol
                            ? {
                                left: isStickyGroupCol
                                  ? stickyLeftOffsets[colIndex] ?? 0
                                  : stickyLeftPx,
                                zIndex: 20 - colIndex,
                              }
                            : {}),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isLinkable && onClickCell) {
                            onClickCell(row, column.dataIndex);
                          } else if (!hasAnyCellHandler && onClickRow) {
                            onClickRow(row);
                          }
                        }}
                      >
                        <div
                          className={`text-gray-900 ${cellAlignClass}`}
                        >
                          {(() => {
                            const val = row[column.dataIndex];
                            const empty = val == null || val === "";

                            // custom render override from column definition
                            if (column.render) {
                              const rendered = column.render(val, row);
                              return rendered === "" ? "-" : rendered;
                            }

                            // currency columns
                            if (CURRENCY_COLS.has(column.dataIndex)) {
                              return !empty && isFinite(Number(val))
                                ? `$${Number(val).toLocaleString("en-US")}`
                                : "-";
                            }

                            // date columns
                            const DATE_COLS = new Set([
                              "date", "available_date",
                              "date_became_rented", "date_became_sold",
                              "previous_sold_date",
                            ]);
                            if (DATE_COLS.has(column.dataIndex)) {
                              return val ? formatDate(val) : "-";
                            }

                            // plain integer columns (bedrooms, sqft, etc.)
                            const INT_COLS = new Set([
                              "bedrooms", "bathrooms", "half_bathrooms",
                              "square_feet", "sf_under_ac",
                            ]);
                            if (INT_COLS.has(column.dataIndex)) {
                              return !empty && isFinite(Number(val))
                                ? Number(val).toLocaleString("en-US")
                                : "-";
                            }

                            // intracoastal view shortening
                            if (column.dataIndex === "view" && typeof val === "string") {
                              return val.toLowerCase() === "intracoastal" ? "intrac" : val;
                            }

                            // any other numeric value → add thousand separators
                            if (!empty && isFinite(Number(val)) && val !== true && val !== false) {
                              return Number(val).toLocaleString("en-US");
                            }

                            // string values or empty
                            return empty ? "-" : val;
                          })()}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                  );
              })}
            </tbody>
          </table>
          )}
        </div>
  );

  if (noInternalScroll) {
    return <>{tableContent}</>;
  }

  return (
    <div className="relative w-full">
      {/* Top scrollbar - always visible on all devices */}
      {showTopScrollbar && <AlwaysVisibleScrollbar scrollContainerRef={scrollContainerRef} statusColor={statusColor} />}
      {/* Table Container - native scrollbar hidden via CSS */}
      <div
        className="overflow-x-auto scrollbar-hidden"
        ref={scrollContainerRef}
        tabIndex={0}
        role="region"
        aria-label="Matrix table horizontal scroll"
        onKeyDown={(e) => {
          const el = scrollContainerRef.current;
          if (!el) return;

          const key = e.key;
          const isArrow = key === "ArrowLeft" || key === "ArrowRight";
          const isPage = key === "PageUp" || key === "PageDown";
          const isHomeEnd = key === "Home" || key === "End";
          if (!isArrow && !isPage && !isHomeEnd) return;

          e.preventDefault();

          const smallStep = e.shiftKey ? 200 : 60;
          const pageStep = Math.max(240, Math.floor(el.clientWidth * 0.9));
          const maxLeft = el.scrollWidth - el.clientWidth;

          if (key === "Home") {
            el.scrollLeft = 0;
            return;
          }
          if (key === "End") {
            el.scrollLeft = maxLeft;
            return;
          }

          const delta =
            key === "ArrowLeft"
              ? -smallStep
              : key === "ArrowRight"
                ? smallStep
                : key === "PageUp"
                  ? -pageStep
                  : pageStep;

          el.scrollLeft = Math.max(0, Math.min(maxLeft, el.scrollLeft + delta));
        }}
      >
        {tableContent}
      </div>
      {/* Bottom scrollbar - always visible on all devices */}
      {showBottomScrollbar && <AlwaysVisibleScrollbar scrollContainerRef={scrollContainerRef} statusColor={statusColor} />}
    </div>
  );
};

// Add editable preview support
const EditableTable = ({ data, editable, onEdit }) => {
  if (!data || data.length === 0) return <div>No data</div>;
  const columns = Object.keys(data[0]).map((key) => ({
    key,
    title: key.replace(/_/g, " ").toUpperCase(),
    dataIndex: key,
  }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left text-gray-500 border-collapse">
        <thead style={{ backgroundColor: "#F9D4C4" }}>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-2 font-bold border text-xs">
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-b">
              {columns.map((col) => (
                <td key={col.key} className="px-2 py-2 border text-xs">
                  {editable ? (
                    <input
                      type="text"
                      value={row[col.key] ?? ""}
                      onChange={(e) => onEdit(rowIdx, col.key, e.target.value)}
                      className="w-full p-1 border rounded"
                    />
                  ) : (
                    row[col.key] ?? ""
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CustomTable;
export { EditableTable };
